import { CART_MODULE } from "@/api_framework/api_config";
import {
  IUrgentCancelSession,
  IUrgentCancelStartPayload,
  IUrgentCancelStartResponse,
} from "@/api_framework/api_modals/Cart";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback } from "react";
import { useAPIUtils } from "../../useAPIUtils";

AxiosCurlirize(axios, () => {});

export type IUrgentCancelStartResult = {
  status: boolean;
  session: IUrgentCancelSession | null;
  /** A session is already open for this invoice and has to be released first. */
  conflict: boolean;
  message: string;
  /** Session id named by the rejection, when the backend reports one. */
  existingSessionId: string | null;
};

const FAILED: IUrgentCancelStartResult = {
  status: false,
  session: null,
  conflict: false,
  message: "",
  existingSessionId: null,
};

/**
 * Opens an urgent-cancel session for an invoice. The backend clones the invoice
 * into a draft cart that the user trims down before checkout.
 */
export const useUrgentCancelStartAPI = () => {
  const configuration = useConfiguration();
  const { captureError, extractErrorMessage } = useAPIUtils();
  const { sendCurl } = useCurlLogger();

  const execute = useCallback(async (payload: IUrgentCancelStartPayload): Promise<IUrgentCancelStartResult> => {
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const response: IUrgentCancelStartResult = await axios
        .post(`${CART_MODULE?.URGENT_CANCEL_START?.baseURL}`, payload, {
          headers: {
            Authorization: `${accessToken}`,
            "Content-Type": "application/json",
          },
        })
        .then((res: AxiosResponse<IUrgentCancelStartResponse>) => {
          if (res.data.status === true && res.data.data?.session_id) {
            return { ...FAILED, status: true, session: res.data.data };
          }
          return { ...FAILED, message: res.data.message };
        })
        .catch((e: AxiosError) => {
          sendCurl(e.config?.["curlCommand"]);
          // A 400 here means a session is already open for this invoice. The
          // modal offers to clear it, so it is surfaced inline rather than as a
          // toast the user can only dismiss.
          if (e?.response?.status === 400) {
            const body = e.response.data as any;
            return {
              ...FAILED,
              conflict: true,
              message: extractErrorMessage(body?.message ?? e.message),
              existingSessionId: body?.data?.session_id ?? body?.session_id ?? null,
            };
          }
          captureError(e);
          return FAILED;
        });
      return response;
    } catch (e: any) {
      captureError(e);
      return FAILED;
    }
  }, []);

  return { execute };
};
